 
// Enhanced Global Search (like ClickUp's universal search)
import { AppIcon } from '@/components/ui/icons';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { IS_DEMO_MODE } from '@/config/env';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveWorkspaceId } from '@/hooks/useActiveWorkspaceId';
import {
  buildAuthorizedDemoSearchResults,
  type AuthorizedSearchResult,
} from '@/lib/search/authorizedDemoSearch';

type SearchResult = AuthorizedSearchResult;

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
}

export const GlobalSearchModal: React.FC<GlobalSearchProps> = ({
  isOpen,
  onClose,
  searchTerm,
  onSearchChange
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const scopedWorkspaceId = useActiveWorkspaceId();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the search input when the modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Showcase search is Demo-only and reduced by exact user/resource scope.
  // Connected environments fail closed until backed by an authoritative
  // server-side search projection.
  const searchResults = useMemo(() => {
    return buildAuthorizedDemoSearchResults({
      user,
      workspaceId: scopedWorkspaceId,
      searchTerm,
      isDemoMode: IS_DEMO_MODE,
    });
  }, [scopedWorkspaceId, searchTerm, user]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev < searchResults.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
          break;
        case 'Enter':
          e.preventDefault();
          if (searchResults[selectedIndex]) {
            navigate(searchResults[selectedIndex].url);
            onClose();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, searchResults, navigate, onClose]);

  // Reset selected index when search results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchResults]);

  const handleResultClick = (result: SearchResult) => {
    navigate(result.url);
    onClose();

    // Add to recent searches
    setRecentSearches(prev => {
      const updated = [searchTerm, ...prev.filter(s => s !== searchTerm)].slice(0, 5);
      localStorage.setItem('recentSearches', JSON.stringify(updated));
      return updated;
    });
  };

  const getTypeColor = (type: SearchResult['type']) => {
    switch (type) {
      case 'project': return 'text-status-info-foreground';
      case 'task': return 'text-status-success-foreground';
      case 'user': return 'text-status-caution-foreground';
      case 'document': return 'text-status-warning-foreground';
      default: return 'text-text-muted';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 overlay-scrim"
          />

          {/* Search Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] w-full max-w-2xl mx-4"
          >
            <div className="bg-surface-card rounded-2xl shadow-surface border border-[color:var(--surface-border)] overflow-hidden">
              {/* Search Input */}
              <div className="p-4 border-b border-border-subtle">
                <div className="relative">
                  <AppIcon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                  <input
                    ref={inputRef}
                    type="text"
                    aria-label="Search"
                    autoComplete="off"
                    placeholder="Search projects, tasks, people..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-transparent border-0 outline-0 text-text-primary placeholder-zinc-500 text-lg"
                  />
                </div>
              </div>

              {/* Search Results */}
              <div className="max-h-96 overflow-y-auto">
                {searchResults.length > 0 ? (
                  <div className="p-2">
                    {searchResults.map((result, index) => (
                      <motion.button
                        key={`${result.type}:${result.id}`}
                        onClick={() => handleResultClick(result)}
                        className={`w-full p-3 rounded-lg text-left transition-colors ${index === selectedIndex
                          ? 'bg-accent-primary/10'
                          : 'hover:bg-surface-muted/50'
                          }`}
                        whileTap={{ scale: 0.99 }}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-xl ${index === selectedIndex
                            ? 'bg-accent-primary/10'
                            : 'bg-surface-muted'
                            }`}>
                            <AppIcon name={result.icon} className={`w-4 h-4 ${index === selectedIndex
                              ? 'text-accent-primary'
                              : getTypeColor(result.type)
                              }`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-text-primary truncate">
                                {result.title}
                              </h4>
                              <span className="text-xs text-text-muted capitalize ml-2">
                                {result.type}
                              </span>
                            </div>
                            {result.subtitle && (
                              <p className="text-sm text-text-muted truncate">
                                {result.subtitle}
                              </p>
                            )}
                            {result.description && (
                              <p className="text-xs text-text-muted mt-1 line-clamp-1">
                                {result.description}
                              </p>
                            )}
                          </div>
                          <AppIcon name="arrowRight" className="w-4 h-4 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </motion.button>
                    ))}
                  </div>
                ) : searchTerm.trim() ? (
                  <div className="p-8 text-center">
                    <AppIcon name="search" className="w-8 h-8 text-text-on-muted mx-auto mb-3" />
                    <p className="text-text-muted">No results found for "{searchTerm}"</p>
                    <p className="text-sm text-text-muted mt-1">Try different keywords or check spelling</p>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <AppIcon name="search" className="mx-auto mb-3 h-8 w-8 text-text-on-muted" />
                    <p className="text-sm font-medium text-text-secondary">Search this workspace</p>
                    <p className="mt-1 text-xs text-text-muted">
                      Results are limited to records available in your active context.
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-3 bg-surface-muted/50 border-t border-border-subtle">
                <div className="flex items-center justify-between text-xs text-text-muted">
                  <div className="flex items-center gap-4">
                    <span><kbd className="px-1.5 py-0.5 bg-surface-chip rounded-sm">↑↓</kbd> Navigate</span>
                    <span><kbd className="px-1.5 py-0.5 bg-surface-chip rounded-sm">⏎</kbd> Select</span>
                  </div>
                  <span><kbd className="px-1.5 py-0.5 bg-surface-chip rounded-sm">Esc</kbd> Close</span>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
